import zxcvbn from 'zxcvbn';

import { getTranslatedText } from '../languages';
import { Message } from '../components/lists/SystemMessages';

export function readArgument(name) {
  return process.argv
    .map(x => x.match(/(.*)=(.*)/))
    .filter(x => !!x)
    .filter(x => x[1] === name)
    .map(x => x[2])[0];
}

export function getApiErrorMessage(error, language) {
  if (!error || !language) {
    throw new Error(`Missing parameters in getApiErrorMessage! error: "${error}", language: "${language}"`);
  }

  const { payload } = error;

  let displayedErrorMessage = getTranslatedText('error-general', 'general/messages', language);

  if (payload) {
    const { translatedMessage } = payload;

    if (translatedMessage) {
      displayedErrorMessage = translatedMessage;
    }
  }

  return new Message('error', displayedErrorMessage);
}

export async function asyncForEach(array, callBack) {
  for (let i = 0, arrayLen = array.length; i < arrayLen; i += 1) {
    await callBack(array[i], i, array);
  }
}

export function focusFirstElement(
  parentDomElement,
  focusableElements = [
    'a[href]',
    'button',
    'input',
    'select',
    'textarea',
    '[tabindex]:not([tabindex="-1"])'
  ]
) {
  const firstFocusableChild = parentDomElement.querySelector(focusableElements.join(', '));

  if (firstFocusableChild) {
    firstFocusableChild.focus();
  }
  else {
    console.warn('focusFirstElement: Unable to find element to focus on');
  }
}

export function mapObject(inputObject, callback) {
  const outputObject = {};

  Object.entries(inputObject).forEach(entry => {
    const {
      0: newKey,
      1: newValue
    } = callback(entry);

    outputObject[newKey] = newValue;
  });

  return outputObject;
}

export function cloneObject(objectToClone, objectToAssign = {}, propsToExclude = []) {
  const outputObject = Object.assign(objectToAssign, objectToClone);

  if (propsToExclude.length > 0) {
    propsToExclude.forEach(excludedProp => delete outputObject[excludedProp]);
  }

  return outputObject;
}

export function getStorableDateString(dateObject) {
  if (Number.isNaN(dateObject)) {
    console.error(`Invalid date object passed: ${JSON.stringify(dateObject)}`);
    return false;
  }

  const month = dateObject.getMonth() + 1;
  const day = dateObject.getDate();
  return [dateObject.getFullYear(), (month > 9 ? '' : '0') + month, (day > 9 ? '' : '0') + day].join('-');
}

export function validatePropTypeDate(props, propName, componentName, isRequired) {
  const value = props[propName];
  const valueType = typeof (value);

  if (isRequired && !value) {
    return new Error(`Error: missing required prop "${propName}" - in ${componentName}`);
  }

  if (value && Object.prototype.toString.call(value) !== '[object Date]') {
    return new Error(`Error: "${propName}" value should be a Date object, "${
      valueType === 'object' ? Object.prototype.toString.call(value) : valueType
    }" was provided - in ${componentName}`);
  }
}

export function validatePropTypeInterval(
  props,
  propName,
  componentName,
  minValue,
  maxValue,
  isRequired
) {
  const value = props[propName];
  const valueType = typeof (value);

  if (isRequired && !value) {
    return new Error(`Error: missing required prop "${propName}" - in ${componentName}`);
  }

  if (value) {
    if (valueType !== 'number') {
      return new Error(`Error: "${propName}" value should be a number, "${
        valueType === 'object' ? Object.prototype.toString.call(value) : valueType
      }" was provided - in ${componentName}`);
    }

    if (value < minValue || value > maxValue) {
      return new Error(`Invalid "${propName}" prop value, supplied: "${value}" - in ${componentName}`);
    }
  }
}

export function manageEventListener(targetDOMElement, eventName, action, callback) {
  switch (action) {
    case 'add':
      targetDOMElement.addEventListener(eventName, callback);
      break;

    case 'remove':
      targetDOMElement.removeEventListener(eventName, callback);
      break;

    default:
      console.error(`"action" is missing or invalid in manageEventListener - got: ${action}`);
      return false;
  }
}

export function handleElementCloserEvents(event, elementSelector, callback) {
  const { key, type } = event;
  const shouldFireCallback = (
    (type === 'keyup' && key === 'Escape')
    || !event.target.closest(elementSelector)
    || !elementSelector
  );

  if (shouldFireCallback) {
    callback(event);
  }
}

export function getNestedValueByPath(object, nestingPathWithSlashes) {
  const parentProps = nestingPathWithSlashes.split('/').filter(key => key !== '');
  const nestedValue = parentProps.reduce((xs, x) => ((xs && xs[x]) ? xs[x] : null), object);

  if (!nestedValue) {
    throw new Error(
      `ERROR: no nested value could be found in the provided nesting path (${
        nestingPathWithSlashes
      })`
    );
  }

  return nestedValue;
}

// todo this is just a placeholder
export function initFacebook() {
  throw new Error('not expecting to call facebook');
}

export async function accessFacebook() {
  return new Promise((resolve, reject) => {
    try {
      FB.login(response => {
        const { authResponse } = response;
        const {
          accessToken,
          userID: facebookId
        } = authResponse;

        if (authResponse && accessToken && facebookId) {
          resolve({
            accessToken,
            facebookId,
            wholeResponse: response
          });
        }
        else {
          reject(new Error('error1'));
        }
      }, { scope: 'email,public_profile' });
    }
    catch (e) {
      reject(new Error('error2'));
    }
  });
}

export function transformFirstLetter(string, mode) {
  let firstLetter = string.charAt(0);

  switch (mode) {
    case 'upper':
      firstLetter = firstLetter.toUpperCase();
      break;

    case 'lower':
      firstLetter = firstLetter.toLowerCase();
      break;

    default:
      throw new Error(`Invalid letter transformation mode "${mode}"`);
  }

  return `${firstLetter}${string.slice(1)}`;
}

export function transformIntoCamelCase(string) {
  const mainParamType = typeof (string);

  if (mainParamType !== 'string') {
    throw new Error(
      `transformIntoCamelCase expects its parameter to be string,
      "${mainParamType}" was passed`
    );
  }

  const dashedString = string.replace(new RegExp('\\s', 'g'), '-');
  const explodedString = dashedString.split('-');

  const transformedWords = explodedString.map((word, index) => (
    index !== 0 ? transformFirstLetter(word, 'upper') : word
  ));

  return transformedWords.join('');
}

export function transformIntoDashCase(string) {
  const mainParamType = typeof (string);

  if (mainParamType !== 'string') {
    throw new Error(
      `transformIntoDashCase expects its parameter to be string,
      "${mainParamType}" was passed`
    );
  }

  return string.replace(/([a-zA-Z])(?=[A-Z])/g, '$1-').toLowerCase();
}

export function getTranslatedPasswordError(password, language = 'en') {
  const getErrorMessage = function (errorReason, translationReplacements) {
    return getTranslatedText(
      `error-input-password-${errorReason}`,
      'general/messages',
      language,
      translationReplacements
    );
  };

  const { score } = zxcvbn(password);

  if (score < 2) {
    return getErrorMessage('weak');
  }

  const minLength = 8;

  if (password.length < minLength) {
    return getErrorMessage('short', { minLength });
  }

  if (!password.match(new RegExp('(?=.*[a-z])'))) {
    return getErrorMessage('missing-lowercase');
  }

  if (!password.match(new RegExp('(?=.*[A-Z])'))) {
    return getErrorMessage('missing-uppercase');
  }

  if (!password.match(new RegExp('(?=.*[0-9])'))) {
    return getErrorMessage('missing-digit');
  }

  return null;
}

export function isObjectEmpty(object) {
  return Object.entries(object).length === 0 && object.constructor === Object;
}

// TODO: Delete "addItemToArrayOnce" everywhere and replace with new Set(...array)!!!
export function addItemToArrayOnce(array, newItem, addToTheFront = false) {
  if (!Array.isArray(array)) {
    throw new Error(`ERROR: expected an array but got "${typeof (array)}"`);
  }

  if (!array.includes(newItem)) {
    array[addToTheFront ? 'unshift' : 'push'](newItem);
  }
}

export function addOrdinalSuffix(number, language) {
  const passedNumberType = typeof (number) !== 'number';

  if (passedNumberType) {
    throw new Error(`Unexpected number passed to addOrdinalSuffix (passed type: ${passedNumberType})`);
  }

  let suffix = '';

  if (language === 'en') {
    const defaultSuffix = 'th';

    if (number < 11 || number > 13) {
      switch (number % 10) {
        case 1:
          suffix = 'st';
          break;

        case 2:
          suffix = 'nd';
          break;

        case 3:
          suffix = 'rd';
          break;

        default:
          suffix = defaultSuffix;
      }
    }

    else {
      suffix = defaultSuffix;
    }
  }

  return `${number}${suffix}`;
}

export function restartPageView() {
  window.scrollTo(0, 0);
  focusFirstElement(document);
  document.activeElement.blur();
}

export const getRandomAmount = function (min, max) {
  return Math.random() * (max - min) + min;
};

export function removeArrayObjectDuplicates(array, conditionCallback) {
  return array.filter((objectA, i, self) => self.findIndex(objectB => conditionCallback(objectA, objectB)) === i);
}

export function flatArrayBrowserCompatibly(array) {
  return [].concat(...array);
}